import { useCallback, useMemo } from 'react'

import { EntityType, SportEntity } from '@utils/entities'
import translate from '@i18n'
import useSnackbar from '@hooks/useSnackbar'
import useSportEntitySubscriptions, {
  SportEntitySubscription,
} from '@hooks/useSportEntitySubscriptions'
import useSportEntitySubscriptionsSubscribe from '@hooks/useSportEntitySubscriptionsSubscribe'
import useSportEntitySubscriptionsUnsubscribe from '@hooks/useSportEntitySubscriptionsUnsubscribe'

import useAuthentication from './useAuthentication'
import useTracking, { TrackingFnType } from './useTracking'
import useUser from './useUser'

export type UseFollowMyTeamType = ({
  entityType,
  enabled,
}: {
  entityType: EntityType
  enabled?: boolean
}) => {
  isFollowing: (entityId: string) => boolean
  toggleFollow: (competitor: SportEntity) => void
}

const useFollowMyTeam: UseFollowMyTeamType = ({ entityType, enabled }) => {
  const user = useUser()
  const { login } = useAuthentication()

  const onFollowEvent = useCallback<
    TrackingFnType<{ entityId: string; entityType: EntityType }>
  >(
    ({ extraData }) => ({
      event: 'follow_team',
      targetContentType: 'entity',
      targetContentSubType: extraData.entityType,
      entity_id: extraData.entityId,
      subscription_options: '',
    }),
    []
  )

  const onUnfollowEvent = useCallback<
    TrackingFnType<{ entityId: string; entityType: EntityType }>
  >(
    ({ extraData }) => ({
      event: 'unfollow_team',
      targetContentType: 'entity',
      targetContentSubType: extraData.entityType,
      entity_id: extraData.entityId,
    }),
    []
  )

  const trackedOnFollow = useTracking(onFollowEvent)
  const trackedOnUnfollow = useTracking(onUnfollowEvent)

  const onLogin = useCallback(
    () => login({ prefix: 'FMT', loginCase: 'email_only' }),
    [login]
  )

  const { data: sportEntitySubscriptions } = useSportEntitySubscriptions({
    entityType,
    enabled,
  })

  const sportEntitySubscriptionsMap = useMemo(
    () =>
      sportEntitySubscriptions?.reduce(
        (acc, subscription) => {
          acc[subscription.entityId] = subscription
          return acc
        },
        {} as Record<string, SportEntitySubscription>
      ),
    [sportEntitySubscriptions]
  )

  const isFollowing = useCallback(
    (entityId: string) => !!sportEntitySubscriptionsMap?.[entityId],
    [sportEntitySubscriptionsMap]
  )

  const hasNotificationsEnabled = useCallback(
    (entityId: string) => {
      const channel = sportEntitySubscriptionsMap?.[entityId]?.channel

      return !!(
        channel &&
        (channel.includes('push_notifications') ||
          channel.includes('live_notifications'))
      )
    },
    [sportEntitySubscriptionsMap]
  )

  const { mutate: subscribeToEntity } = useSportEntitySubscriptionsSubscribe()

  const { mutate: unsubscribeFromEntity } =
    useSportEntitySubscriptionsUnsubscribe()

  const { showSnackbar } = useSnackbar()

  const follow = useCallback(
    (sportEntity: SportEntity) => {
      subscribeToEntity(
        {
          ...sportEntity,
          channel: null,
        },
        {
          onSuccess: () => {
            showSnackbar(
              translate('followMyTeam.success.follow', {
                teamName: sportEntity.title,
              }),
              'success'
            )
          },
          onError: () => {
            showSnackbar(translate('followMyTeam.error.saveFailed'), 'error')
          },
        }
      )
      trackedOnFollow({
        entityId: sportEntity.entityId,
        entityType: sportEntity.entityType,
      })
    },
    [subscribeToEntity, showSnackbar, trackedOnFollow]
  )

  const unfollow = useCallback(
    (sportEntity: SportEntity) => {
      if (hasNotificationsEnabled(sportEntity.entityId)) {
        showSnackbar(
          translate('followMyTeam.error.cannotUnfollowFromWeb', {
            teamName: sportEntity.title,
          }),
          'error'
        )
        return
      }

      unsubscribeFromEntity(
        {
          entityId: sportEntity.entityId,
          entityType: sportEntity.entityType,
        },
        {
          onSuccess: () => {
            showSnackbar(
              translate('followMyTeam.success.unfollow', {
                teamName: sportEntity.title,
              }),
              'success'
            )
          },
          onError: () => {
            showSnackbar(translate('followMyTeam.error.saveFailed'), 'error')
          },
        }
      )
      trackedOnUnfollow({
        entityId: sportEntity.entityId,
        entityType: sportEntity.entityType,
      })
    },
    [
      unsubscribeFromEntity,
      showSnackbar,
      trackedOnUnfollow,
      hasNotificationsEnabled,
    ]
  )

  const toggleFollow = useCallback(
    (sportEntity: SportEntity) => {
      if (!user) {
        onLogin()
      } else if (isFollowing(sportEntity.entityId)) {
        unfollow(sportEntity)
      } else {
        follow(sportEntity)
      }
    },
    [isFollowing, follow, unfollow, user, onLogin]
  )

  return {
    isFollowing,
    toggleFollow,
  }
}

export default useFollowMyTeam
